import React, { useRef, useState, useEffect } from 'react';
import MapFrame from '../../components/MapFrame';
import RSVPForm        from '../../components/RSVPForm';
import Wishlist        from '../../components/Wishlist';
import YandexMap       from '../../components/YandexMap';
import Countdown       from '../../components/Countdown';
import FixedCTA        from '../../components/FixedCTA';
import useReveal       from '../../components/useReveal';
import Schedule        from '../../components/Schedule';
import DresscodePalette from '../../components/DresscodePalette';
import CalendarButton  from '../../components/CalendarButton';
import BlockSubtitle   from '../../components/BlockSubtitle';
import PaidWatermark   from '../../components/PaidWatermark';
import MusicPlayer     from '../../components/MusicPlayer';
import './Flora.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const src = u => !u ? null : u;
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;

const fmtDateDots = raw => {
  if (!raw) return '';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '';
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join('.');
};

/* Load self-hosted Great Vibes once. Injected as a <style> with absolute
   public URLs so the browser resolves them at runtime (CRA's css-loader
   would otherwise try to bundle the woff2 files and fail). */
function useFloraFonts() {
  useEffect(() => {
    const id = 'fl-great-vibes-font';
    if (document.getElementById(id)) return;
    const base = '/templates/flora/fonts';
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
@font-face {
  font-family: 'Great Vibes';
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url('${base}/great-vibes-cyr-ext.woff2') format('woff2');
  unicode-range: U+0460-052F, U+1C80-1C8A, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F;
}
@font-face {
  font-family: 'Great Vibes';
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url('${base}/great-vibes-cyr.woff2') format('woff2');
  unicode-range: U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116;
}
@font-face {
  font-family: 'Great Vibes';
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url('${base}/great-vibes-latin-ext.woff2') format('woff2');
  unicode-range: U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
}
@font-face {
  font-family: 'Great Vibes';
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url('${base}/great-vibes-latin.woff2') format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}`;
    document.head.appendChild(style);
  }, []);
}

/* ── Class maps for shared components ─────────────────────────── */
const FL_CD_CLASSES    = { root: 'fl-cd',    cell: 'fl-cd-cell', num: 'fl-cd-num', label: 'fl-cd-lbl' };
const FL_SCHED_CLASSES = { root: 'fl-sched', row: 'fl-sched-row', rowBorder: 'fl-sched-row--border', time: 'fl-sched-time', info: 'fl-sched-info', name: 'fl-sched-name', desc: 'fl-sched-desc' };
const FL_DRESS_CLASSES = { wrap: 'fl-swatches', swatchWrap: 'fl-swatch-wrap', swatch: 'fl-swatch', swatchLabel: 'fl-swatch-lbl', photos: 'fl-dc-photos', photo: 'fl-dc-photo' };
const FL_CD_LABELS     = { d: 'Дней', h: 'Часов', m: 'Минут', s: 'Секунд' };
const FL_RSVP_CLASSES = {
  form:    'fl-form',
  tabs:    'fl-rsvp-tabs',
  tab:     'fl-rsvp-tab',
  tabSel:  'fl-rsvp-tab--sel',
  field:   'fl-form-field',
  label:   'fl-form-lbl',
  input:   'fl-form-input',
  submit:  'fl-btn fl-btn--submit fl-btn--full',
  success: 'fl-form-ok',
  error:   'fl-form-err',
  chip:    'fl-chip',
  chipSel: 'fl-chip--sel',
  chips:   'fl-form-chips',
};
const FL_WISHLIST_CLASSES = {
  list:        'fl-wish',
  item:        'fl-wish-item',
  itemBorder:  '',
  row:         'fl-wish-row',
  name:        'fl-wish-name',
  price:       'fl-wish-price',
  btn:         'fl-btn fl-btn--soft fl-btn--full',
  btnReserved: 'fl-btn--res',
};
const FL_WISHLIST_MODAL_CLASSES = {
  bg:         'fl-wish-modal-bg',
  modal:      'fl-wish-modal',
  title:      'fl-wish-modal-title',
  subtitle:   'fl-wish-modal-sub',
  input:      'fl-form-input',
  error:      'fl-form-err',
  btns:       'fl-wish-modal-btns',
  btnCancel:  'fl-btn fl-btn--outline',
  btnConfirm: 'fl-btn fl-btn--green',
};

const ASSET = '/templates/flora';

/* Decorative leaf cluster used in the corners of tinted sections. */
function CornerLeaves({ variant }) {
  return (
    <div className={`fl-corner fl-corner--${variant}`} aria-hidden="true">
      <img className="fl-corner-a" src={`${ASSET}/sprig-043.png`} alt="" />
      <img className="fl-corner-b" src={`${ASSET}/sprig-041.png`} alt="" />
      <img className="fl-corner-c" src={`${ASSET}/sprig-064.png`} alt="" />
    </div>
  );
}

/* ══ Main Template ═════════════════════════════════════════════ */
export default function Flora({ project, design, isPreview }) {
  const pageRef  = useRef(null);
  /* В зависимость включаем и наличие фото: секции с фото появляются
     динамически (после загрузки в превью) — иначе reveal-обсёрвер их не
     подхватывает и они остаются скрытыми (opacity: 0). */
  const blockDep = `${project?.blocks?.length}|${project?.cover_photos?.length || 0}|${project?.footer_photo?.url ? 1 : 0}|${project?.dresscode_photos?.length || 0}`;
  useReveal(pageRef, blockDep, { revealSelector: '.fl-reveal', inClass: 'fl-in' });
  useFloraFonts();

  const [inPreview, setInPreview] = useState(!!isPreview);
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    setInPreview(!!isPreview || !!root.closest('.lp-phone-screen,.lp-template-inner,[class*="lp-"]'));
  }, [isPreview]);

  const p = project || {};
  const d = design  || {};

  /* Colours — driven by the user's palette so chat-bot/constructor edits
     apply live. Flora maps its botanical roles onto the standard palette:
       primary   → green (text + tinted-section dark elements)
       secondary → cream (tinted-section background)
       accent    → gold  (the • accent)
     We deliberately use `primary` (not `textDark`) for body text, because
     the default textDark is a near-black (#1E1E1E) that would override the
     template's signature dark-green — primary defaults to #2B3C2C, which
     matches the Figma mock, and still updates when the user picks a palette. */
  const pal   = d.palette || p.palette || {};
  const green = pal.primary   || '#2B3C2C';
  const cream = pal.secondary || '#ECF2E6';
  const fg    = pal.primary   || '#2B3C2C';
  const gold  = pal.accent    || '#FFD58B';

  /* Blocks */
  const blocks = p.blocks || ['menu','map','schedule','palette','rsvp','survey','message','wishlist','countdown','calendar'];
  const HAS_MENU     = blocks.includes('menu');
  const HAS_CALENDAR = blocks.includes('calendar');

  /* Text */
  const bride       = p.bride || '';
  const groom       = p.groom || '';
  const tagline     = p.block_content?.header?.subtitle || '';
  const footerQuote = p.block_content?.footer?.quote    || '';
  const invText     = p.invitation_text || p.block_content?.message?.text || '';
  const invTitle    = p.block_content?.message?.title || 'Дорогие гости';

  /* Photos */
  const photos      = p.cover_photos || (p.cover_photo ? [{ url: p.cover_photo }] : []);
  const heroPhoto   = src(photos[0]?.url) || null;
  const heroPhotoMeta = photos[0] || null;
  const dcPhotos    = p.dresscode_photos || [];
  const footerPhotoObj = p.footer_photo?.url ? p.footer_photo : null;
  const footerPhoto    = src(footerPhotoObj?.url) || null;

  /* Date */
  const dateStr = fmtDateDots(p.wedding_date);

  /* Schedule / venues / dresscode / wishlist */
  const schedule = p.schedule?.length ? p.schedule : [];
  const venues   = p.venues || [];
  const swatches = p.dresscode_palette || [];
  const wishlist = p.wishlist || [];

  /* Block content titles */
  const bc = p.block_content || {};
  const titleSchedule = bc.schedule?.title  || 'Программа';
  const titleLocation = bc.map?.title       || 'Локация';
  const titleDress    = bc.palette?.title   || 'Дресс-код';
  const titleRsvp     = bc.rsvp?.title      || 'Подтвердите присутствие';
  const titleWish     = bc.wishlist?.title  || 'Что дарить';
  const titleCD       = bc.countdown?.title || 'До нашей свадьбы';
  const calBtn        = bc.calendar?.title  || 'Добавить в календарь';

  const HAS = id => blocks.includes(id);

  /* Wavy section dividers (paths from the Figma design). Inline SVG so the
     fill follows the cream palette colour and recolours with the user's
     chosen palette instead of staying the #D9D9D9 placeholder. */
  const DividerDown = () => (
    <div className="fl-divider fl-divider--down" aria-hidden="true">
      <svg viewBox="0 0 390 91" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.43226e-06 0C0.000816602 32.6828 87.305 59.1776 195 59.1776C302.695 59.1776 389.999 32.6828 390 4.21688e-05V91L0 91L6.43226e-06 0Z" fill="var(--fl-cream)" />
      </svg>
    </div>
  );
  const DividerUp = () => (
    <div className="fl-divider fl-divider--up" aria-hidden="true">
      <svg viewBox="0 0 390 58.8887" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M195 0C302.695 0 390 26.3653 390 58.8887H0C0.000389122 26.3653 87.3047 0 195 0Z" fill="var(--fl-cream)" />
      </svg>
    </div>
  );

  return (
    <div className={`fl-page${inPreview ? ' fl-page--preview' : ''}`} ref={pageRef}
      style={{ '--fl-green': green, '--fl-cream': cream, '--fl-fg': fg, '--fl-gold': gold }}>
      <PaidWatermark isPaid={project?.is_paid} isPreview={inPreview} />

      {/* Navigation */}
      {HAS_MENU && (
        <nav className="fl-nav">
          {HAS('schedule') && <a href="#fl-schedule">{bc.menu?.schedule || 'Программа'}</a>}
          {HAS('map')      && <a href="#fl-location">{bc.menu?.location || 'Локация'}</a>}
          {HAS('palette')  && <a href="#fl-dresscode">{bc.menu?.dresscode || 'Дресс-код'}</a>}
          {HAS('rsvp')     && <a href="#fl-rsvp">{bc.menu?.rsvp || 'Анкета'}</a>}
        </nav>
      )}

      {/* Hero — wreath + names + tagline */}
      <section className="fl-hero fl-reveal">
        <div className="fl-hero-box">
          <img className="fl-hero-wreath" src={`${ASSET}/hero-wreath.png`} alt="" aria-hidden="true" />
          <div className="fl-hero-content">
            <div className="fl-hero-names">
              {bride && <span className="fl-hero-name">{bride}</span>}
              <span className="fl-hero-amp">{bc.header?.amp || '&'}</span>
              {groom && <span className="fl-hero-name">{groom}</span>}
            </div>
            <span className="fl-hero-dot">•</span>
            {tagline && <p className="fl-hero-tagline">{tagline}</p>}
          </div>
        </div>
      </section>

      {/* Invitation letter */}
      <section className="fl-invite fl-reveal">
        <div className="fl-in">
          <h2 className="fl-script fl-invite-title">{invTitle}</h2>
          <div className="fl-invite-body">
            {invText
              ? invText.split('\n').map((line, i) =>
                  line.trim() ? <p key={i}>{line}</p> : <br key={i} />)
              : null}
          </div>
        </div>
      </section>

      {/* Cover photo */}
      {heroPhoto && (
        <section className="fl-photo-sect fl-reveal">
          <div className="fl-in">
            <div className="fl-photo">
              <img src={heroPhoto} alt="" style={{ filter: flt(heroPhotoMeta), objectPosition: objPos(heroPhotoMeta) }} />
            </div>
          </div>
        </section>
      )}

      {/* Date + calendar (tinted) */}
      <DividerDown />
      <section className="fl-tint fl-date-sect fl-reveal">
        <CornerLeaves variant="left" />
        <CornerLeaves variant="right" />
        <div className="fl-in fl-date-in">
          {dateStr && <p className="fl-script fl-date">{dateStr}</p>}
          {HAS_CALENDAR && (
            <CalendarButton
              bride={bride} groom={groom} date={p.wedding_date} time={p.wedding_time}
              venues={p.venues} address={p.wedding_address}
              className="fl-btn fl-btn--green fl-date-cal">
              {calBtn}
            </CalendarButton>
          )}
        </div>
      </section>
      <DividerUp />

      {/* Location */}
      {HAS('map') && (
        <section className="fl-loc-sect fl-reveal" id="fl-location">
          <div className="fl-in">
            <h2 className="fl-script fl-h">{titleLocation}</h2>
            <BlockSubtitle text={bc.map?.subtitle} className="fl-p fl-center" />
            {venues.map((v, i) => (
              <p key={i} className="fl-p fl-center">{v.name && v.address ? `${v.name}, ${v.address}` : (v.name || v.address)}</p>
            ))}
            {!venues.some(v => v.address) && p.wedding_address && (
              <p className="fl-p fl-center">{p.wedding_address}</p>
            )}
            {/* Карта — только при наличии адреса (имя без адреса → без карты). */}
            {(p.map_url || (p.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
            <div className="fl-map">
              {(() => {
                if (p.map_url) {
                  return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={p.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                }
                const withCoords = venues.filter(v => Number.isFinite(+v.lat) && Number.isFinite(+v.lon));
                if (withCoords.length > 0) {
                  return <YandexMap points={withCoords} singleZoom={16} region={p.region} provider={bc.map?.provider} preset="islands#greenIcon" />;
                }
                const addr = venues.length > 0
                  ? venues.map(v => v.address).filter(Boolean).join(' — ')
                  : (p.wedding_address || '');
                if (!addr) return null;
                const s = `https://yandex.ru/map-widget/v1/?mode=search&text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={s} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
              })()}
            </div>
            )}
          </div>
        </section>
      )}

      {/* Schedule */}
      {HAS('schedule') && (
        <section className="fl-sched-sect fl-reveal" id="fl-schedule">
          <div className="fl-in">
            <h2 className="fl-script fl-h">{titleSchedule}</h2>
            <BlockSubtitle text={bc.schedule?.subtitle} className="fl-p fl-center" />
            <Schedule items={schedule} classes={FL_SCHED_CLASSES} />
          </div>
        </section>
      )}

      {/* Dresscode (tinted) */}
      {HAS('palette') && (
        <>
          <DividerDown />
          <section className="fl-tint fl-dress-sect fl-reveal" id="fl-dresscode">
            <img className="fl-dress-wreath fl-dress-wreath--r" src={`${ASSET}/wreath-111.png`} alt="" aria-hidden="true" />
            <img className="fl-dress-wreath fl-dress-wreath--l" src={`${ASSET}/wreath-111.png`} alt="" aria-hidden="true" />
            <div className="fl-in">
              <h2 className="fl-script fl-h">{titleDress}</h2>
              <BlockSubtitle text={bc.palette?.subtitle} className="fl-p fl-center" />
              <DresscodePalette
                swatches={swatches}
                photos={dcPhotos}
                cssFilter={flt}
                classes={FL_DRESS_CLASSES}
              />
            </div>
          </section>
          <DividerUp />
        </>
      )}

      {/* RSVP */}
      {HAS('rsvp') && (
        <section className="fl-rsvp-sect fl-reveal" id="fl-rsvp">
          <div className="fl-in">
            <h2 className="fl-script fl-h">{titleRsvp}</h2>
            <BlockSubtitle text={bc.rsvp?.subtitle} className="fl-p fl-center" />
            <RSVPForm project={p} blocks={blocks} classes={FL_RSVP_CLASSES} />
          </div>
        </section>
      )}

      {/* Wishlist */}
      {HAS('wishlist') && (
        <section className="fl-wish-sect fl-reveal">
          <div className="fl-in">
            <h2 className="fl-script fl-h">{titleWish}</h2>
            <BlockSubtitle text={bc.wishlist?.subtitle} className="fl-p fl-center" />
            <Wishlist
              items={wishlist} currency={bc.wishlist?.currency} reserveText={bc.wishlist?.reserveText}
              slug={p.slug}
              classes={FL_WISHLIST_CLASSES}
              modalClasses={FL_WISHLIST_MODAL_CLASSES}
            />
          </div>
        </section>
      )}

      {/* Countdown */}
      {HAS('countdown') && (
        <section className="fl-cd-sect fl-reveal">
          <div className="fl-in">
            <h2 className="fl-script fl-h">{titleCD}</h2>
            <BlockSubtitle text={bc.countdown?.subtitle} className="fl-p fl-center" />
            <Countdown date={p.wedding_date} time={p.wedding_time} bc={bc} classes={FL_CD_CLASSES} labels={FL_CD_LABELS} />
          </div>
        </section>
      )}

      {/* Footer photo */}
      {footerPhoto && (
        <section className="fl-photo-sect fl-reveal">
          <div className="fl-in">
            <div className="fl-photo">
              <img src={footerPhoto} alt="" style={{ filter: flt(footerPhotoObj), objectPosition: objPos(footerPhotoObj) }} />
            </div>
          </div>
        </section>
      )}

      {/* Footer (tinted) */}
      <DividerDown />
      <footer className="fl-tint fl-footer fl-reveal">
        {/* Decorative leaf composition from Figma node 13320:999 — a big
            wreath-10 sprig + a small sprig-04, mirrored on both corners. */}
        <span className="fl-foot-deco fl-foot-deco--l" aria-hidden="true">
          <img className="fl-foot-wreath" src={`${ASSET}/wreath-10.png`} alt="" />
          <img className="fl-foot-sprig"  src={`${ASSET}/sprig-04.png`}  alt="" />
        </span>
        <span className="fl-foot-deco fl-foot-deco--r" aria-hidden="true">
          <img className="fl-foot-wreath" src={`${ASSET}/wreath-10.png`} alt="" />
          <img className="fl-foot-sprig"  src={`${ASSET}/sprig-04.png`}  alt="" />
        </span>
        <p className="fl-script fl-footer-text">
          {(footerQuote || 'Спасибо, что вы с нами').split('\n').map((line, i, arr) => (
            <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
          ))}
        </p>
      </footer>

      {/* Floating RSVP CTA */}
      {HAS('rsvp') && (
        <FixedCTA
          href="#fl-rsvp"
          targetId="fl-rsvp"
          scrollRoot={pageRef}
          className="fl-fixed-cta">
          {bc.rsvp?.ctaText || 'Подтвердить присутствие'}
        </FixedCTA>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={inPreview} />
      )}
    </div>
  );
}
